import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  createExceptionReport,
  fetchExceptions,
  fetchExceptionsForBatch,
  resolveException,
  waiveException,
} from './api';
import type {
  CreateExceptionInput,
  ExceptionListMeta,
  ExceptionListQuery,
  ExceptionReportDto,
  ResolveInput,
} from './types';

export const EXCEPTIONS_QUERY_KEY = 'exceptions' as const;

export const useExceptions = (params: ExceptionListQuery = {}) => {
  return useQuery<{ data: ExceptionReportDto[]; meta: ExceptionListMeta }, ApiClientError>({
    queryKey: [EXCEPTIONS_QUERY_KEY, 'list', params],
    queryFn: () => fetchExceptions(params),
    placeholderData: (prev) => prev,
  });
};

export const useExceptionsForBatch = (batchId: string | undefined) => {
  return useQuery<ExceptionReportDto[], ApiClientError>({
    queryKey: [EXCEPTIONS_QUERY_KEY, 'batch', batchId],
    queryFn: () => fetchExceptionsForBatch(batchId!),
    enabled: !!batchId,
  });
};

export const useResolveException = () => {
  const qc = useQueryClient();
  return useMutation<
    ExceptionReportDto,
    ApiClientError,
    { exceptionId: string; body: ResolveInput }
  >({
    mutationFn: ({ exceptionId, body }) => resolveException(exceptionId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [EXCEPTIONS_QUERY_KEY] }),
  });
};

export const useCreateException = () => {
  const qc = useQueryClient();
  return useMutation<
    ExceptionReportDto,
    ApiClientError,
    { batchId: string; body: CreateExceptionInput }
  >({
    mutationFn: ({ batchId, body }) => createExceptionReport(batchId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [EXCEPTIONS_QUERY_KEY] }),
  });
};

export const useWaiveException = () => {
  const qc = useQueryClient();
  return useMutation<
    ExceptionReportDto,
    ApiClientError,
    { exceptionId: string; body: ResolveInput }
  >({
    mutationFn: ({ exceptionId, body }) => waiveException(exceptionId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [EXCEPTIONS_QUERY_KEY] }),
  });
};
